/**
 * usePullToRefresh — Pull-to-refresh touch handling for mobile PWA.
 * Extracted from useMainApp for maintainability.
 */
import React, { useState, useEffect, useRef } from "react";
import { statusBarInset } from "../utils/iosStatusBar";
import { useAppTheme } from "../contexts/ThemeContext";
import { traceUpdate } from "./useServiceWorkerUpdate";

const PULL_THRESHOLD = 80;
const MIN_SPINNER_MS = 600;
const INSTALL_WAIT_CAP_MS = 5000;
// reg.update() fetches sw.js from the origin and has no deadline of its own:
// on a slow mobile line it alone kept the spinner turning for ~7 s (2026-10-19)
// before reloading onto the same build. Past the cap the reload goes ahead; a
// worker found later is offered by the banner as usual.
export const UPDATE_CHECK_CAP_MS = 3000;

// Kick an SW update check and reload only once the resulting install (if any)
// has reached `waiting`. Reloading on a blind timer raced the install: the
// post-reload page missed the in-flight `updatefound` (it fired before that
// page existed), the finished install sat unnoticed in `waiting`, and the
// user stayed on the old build while believing the pull refreshed them.
// Landing the reload with `reg.waiting` already set lets index.html's
// boot-window auto-activate swap versions silently.
export async function refreshWithSwUpdate() {
  const startedAt = Date.now();
  // Update trace: the pull is a user action like a banner tap; its reload is
  // the step that can queue behind a worker in `activating`.
  traceUpdate("pull");
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (reg) {
      await Promise.race([
        reg.update().catch(() => {}),
        new Promise((resolve) => setTimeout(resolve, UPDATE_CHECK_CAP_MS)),
      ]);
      const installing = reg.installing;
      if (installing) {
        await new Promise((resolve) => {
          const cap = setTimeout(resolve, INSTALL_WAIT_CAP_MS);
          installing.addEventListener("statechange", () => {
            // 'installed' (ready to activate) and 'redundant' (install
            // failed) both end the wait — only 'installing' keeps it open
            if (installing.state !== "installing") {
              clearTimeout(cap);
              resolve();
            }
          });
        });
      }
    }
  } catch {}
  const wait = Math.max(0, MIN_SPINNER_MS - (Date.now() - startedAt));
  setTimeout(() => {
    traceUpdate("reload", { by: "pull" });
    window.location.reload();
  }, wait);
}

function getScrollTop() {
  return Math.max(window.scrollY || 0, document.documentElement.scrollTop || 0, document.body.scrollTop || 0);
}

function isInScrollableContainer(el) {
  let node = el;
  while (node && node !== document.body && node !== document.documentElement) {
    if (node.scrollHeight > node.clientHeight) {
      const ov = window.getComputedStyle(node).overflowY;
      if (ov === "auto" || ov === "scroll") return true;
    }
    node = node.parentElement;
  }
  return false;
}

export function usePullToRefresh() {
  const [pullY, setPullY] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const touchStartY = useRef(0);
  const isPulling = useRef(false);
  const pullYRef = useRef(0);
  const refreshingRef = useRef(false);

  useEffect(() => {
    const onTouchStart = (e) => {
      if (getScrollTop() <= 0 && !refreshingRef.current
          && document.body.style.overflow !== "hidden"
          && !isInScrollableContainer(e.target)) {
        touchStartY.current = e.touches[0].clientY;
        isPulling.current = true;
      }
    };
    const onTouchMove = (e) => {
      if (!isPulling.current) return;
      const dy = e.touches[0].clientY - touchStartY.current;
      if (dy > 0 && getScrollTop() <= 0) {
        e.preventDefault();
        const val = Math.min(dy * 0.4, PULL_THRESHOLD + 20);
        pullYRef.current = val;
        setPullY(val);
      } else {
        isPulling.current = false;
        pullYRef.current = 0;
        setPullY(0);
      }
    };
    const onTouchEnd = () => {
      if (!isPulling.current) return;
      isPulling.current = false;
      if (pullYRef.current >= PULL_THRESHOLD) {
        refreshingRef.current = true;
        setRefreshing(true);
        setPullY(PULL_THRESHOLD);
        if ("serviceWorker" in navigator) {
          refreshWithSwUpdate();
        } else {
          setTimeout(() => window.location.reload(), MIN_SPINNER_MS);
        }
      } else {
        pullYRef.current = 0;
        setPullY(0);
      }
    };
    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchmove", onTouchMove, { passive: false });
    document.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
    };
  }, []);

  return { pullY, refreshing, PULL_THRESHOLD };
}

/**
 * PullToRefreshIndicator — small fixed-position spinner that pairs with
 * usePullToRefresh. Renders nothing until the user starts pulling.
 *
 * Extracted from MainApp + GymSelector + PlatformAdmin so all three top-level
 * shells share the same visual / animation / threshold-feedback behavior — if
 * the styling ever needs to change (e.g. theme-aware shadow), one edit covers
 * all entry points.
 */
export function PullToRefreshIndicator({ pullY, refreshing, PULL_THRESHOLD, }) {
  const { th: theme } = useAppTheme();
  if (pullY <= 0 && !refreshing) return null;
  const cardBg = theme?.card || "#fff";
  return (
    <>
      {/* The spinner is pinned to the top edge of a web view that, on iPad,
          runs under the status bar — otherwise the wheel spins behind the
          clock. 0 everywhere else (see utils/iosStatusBar.js). */}
      <div style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 999, paddingTop: statusBarInset(), display: "flex", justifyContent: "center", pointerEvents: "none" }}>
        <div style={{
          marginTop: Math.min(pullY, PULL_THRESHOLD) - 36,
          width: 36, height: 36, borderRadius: "50%",
          background: cardBg, boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: refreshing ? "none" : "margin-top 0.1s",
          opacity: pullY > 10 ? 1 : 0,
        }}>
          <span style={{
            display: "inline-block", fontSize: 16,
            transform: `rotate(${refreshing ? 0 : Math.min(pullY / PULL_THRESHOLD, 1) * 360}deg)`,
            animation: refreshing ? "ptr-spin 0.8s linear infinite" : "none",
          }}>↻</span>
        </div>
      </div>
      <style>{`@keyframes ptr-spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
}
